<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Diálogo do formulário ÚNICO de campanha (decisão 28). Abre o CampaignForm novo
// para WhatsApp por QR code, WhatsApp oficial e SMS: o canal passou a ser o
// primeiro bloco do formulário, em vez de um formulário por página.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignForm/CampaignForm.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  // waha | official | sms. A página de SMS abre já em SMS.
  initialChannel: { type: String, default: 'waha' },
  title: { type: String, default: '' },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Família de canais da entrada (decisão 30). Vem preenchida quando a pessoa
// chegou pelo cartão "Nova ação de marketing" do mundo Marketing, que manda
// ?new=1 para a rota do canal escolhido. Vazia quando ela clicou em "Nova
// campanha" dentro de Campanhas: aí o passo 1 aparece inteiro.
const channelFamily = ref([]);

const dialogRef = ref(null);
const confirmCloseRef = ref(null);
const formRef = ref(null);
const saving = ref(false);
const saveError = ref('');
// Campanha criada a partir dos cartões marcados: o aviso oferece a volta ao quadro.
const createdFromKanban = ref(false);

const handleSubmit = async ({ payload, start }) => {
  if (saving.value) return;
  saving.value = true;
  saveError.value = '';
  try {
    const campaign = await store.dispatch('campaigns/create', payload);
    if (start && campaign?.id) await CampaignsAPI.start(campaign.id);

    createdFromKanban.value = payload.audience_type === 'kanban_items';
    formRef.value?.reset?.();
    dialogRef.value?.close();
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    useAlert(
      start
        ? 'Campanha criada e iniciada. Dá para acompanhar e pausar em Campanhas.'
        : 'Rascunho salvo. A campanha só sai quando vocês clicarem em Iniciar.'
    );
  } catch (error) {
    saveError.value =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
  } finally {
    saving.value = false;
  }
};

const handleRequestClose = () => {
  if (saving.value) return;
  if (formRef.value?.isDirty?.()) confirmCloseRef.value?.open();
  else dialogRef.value?.close();
};

const confirmAbandon = () => {
  if (saving.value) return;
  confirmCloseRef.value?.close();
  dialogRef.value?.close();
};

const backToKanban = () => {
  createdFromKanban.value = false;
  router?.push({
    name: 'kanban_board',
    params: { accountId: route?.params?.accountId },
  });
};

defineExpose({
  open: ({ channelFamily: family = [] } = {}) => {
    channelFamily.value = family;
    dialogRef.value?.open();
  },
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title || 'Nova campanha'"
    width="6xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    @request-close="handleRequestClose"
  >
    <p v-if="saveError" role="alert" class="text-sm text-n-ruby-11">
      {{ saveError }}
    </p>
    <fieldset class="p-0 m-0 border-0 min-w-0" :disabled="saving">
      <CampaignForm
        ref="formRef"
        :initial-channel="props.initialChannel"
        :channel-family="channelFamily"
        :submitting="saving"
        @submit="handleSubmit"
      />
    </fieldset>
  </Dialog>

  <div
    v-if="createdFromKanban"
    class="flex flex-wrap items-center gap-3 p-3 mt-3 text-sm border rounded-xl border-n-weak bg-n-alpha-2 text-n-slate-11"
    role="status"
  >
    <span>A campanha foi criada com os cartões que vocês marcaram.</span>
    <Button
      label="Voltar ao Kanban"
      variant="faded"
      color="slate"
      size="sm"
      @click="backToKanban"
    />
  </div>

  <Dialog
    ref="confirmCloseRef"
    type="alert"
    :title="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.TITLE')"
    :description="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.CREATE.ABANDON.CANCEL')"
    @confirm="confirmAbandon"
  />
</template>
